
const SENSITIVE_KEY_PATTERNS = [
  /authorization/i,
  /cookie/i,
  /password/i,
  /token/i,
  /refreshtoken/i,
  /accesstoken/i,
  /clientsecret/i,
  /apikey/i,
  /certificate/i,
  /secret/i,
  /cvv/i,
  /creditcard/i,
  /cardnumber/i,
  /accountnumber/i,
  /pixkey/i,
  /email/i,
  /phone/i,
  /taxid/i,
  /cpf/i,
  /cnpj/i,
  /birthdate/i,
  /address/i,
];

export const isSensitiveKey = (key: string): boolean => {
  const normalized = key.replace(/[-_]/g, '').toLowerCase();

  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(normalized));
};

const MAX_REDACTION_DEPTH = 6;

const redactText = (value: string): string =>
  value
    .replace(/bearer\s+[a-zA-Z0-9._-]+/gi, 'Bearer [REDACTED]')
    .replace(
      /([?&](?:[^=&#\s]*(?:token|secret|password|pixkey|api[_-]?key)[^=&#\s]*)=)[^&#\s]*/gi,
      '$1[REDACTED]',
    );

export const redactFrontendData = <T>(data: T): T => {
  const visited = new WeakSet<object>();

  const redact = (value: unknown, depth: number): unknown => {
    if (depth > MAX_REDACTION_DEPTH) {
      return '[TRUNCATED]';
    }

    if (typeof value === 'string') {
      return redactText(value);
    }

    if (value === null || typeof value !== 'object') {
      return value;
    }

    if (visited.has(value)) {
      return '[CIRCULAR]';
    }

    visited.add(value);

    if (Array.isArray(value)) {
      return value.map((item) => redact(item, depth + 1));
    }

    const record: Record<string, unknown> = value instanceof Error
      ? { name: value.name, message: value.message, stack: value.stack }
      : Object.fromEntries(Object.entries(value));

    return Object.fromEntries(
      Object.entries(record).map(([key, item]) => [
        key,
        isSensitiveKey(key) ? '[REDACTED]' : redact(item, depth + 1),
      ]),
    );
  };

  return redact(data, 0) as T;
};

type SentryModule = typeof import('@sentry/react');
let sentryModulePromise: Promise<SentryModule> | null = null;
let sentryInitialized = false;

const loadSentry = async (): Promise<SentryModule | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/react');
  }

  return sentryModulePromise;
};

export const initFrontendSentry = () => {
  const dsn = process.env.SENTRY_DSN;
  const environment = process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV ?? 'development';
  const release = process.env.SENTRY_RELEASE ?? process.env.RELEASE_VERSION ?? 'nexo-web@dev';

  if (!dsn || typeof window === 'undefined') {
    return;
  }

  loadSentry()
    .then((sentry) => {
      if (!sentry || sentryInitialized) {
        return;
      }

      sentry.init({
        dsn,
        environment,
        release,
        tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
        beforeSend(event) {
          return redactFrontendData(event);
        },
      });

      sentryInitialized = true;
    })
    .catch(() => undefined);
};

export const reportFrontendMetric = (metric: { name: string; value: number; rating: string }) => {
  if (!process.env.SENTRY_DSN) {
    return;
  }

  loadSentry().then((sentry) => {
    if (sentry && sentryInitialized) {
      sentry.metrics.distribution(`web.vitals.${metric.name.toLowerCase()}`, metric.value, {
        unit: metric.name === 'CLS' ? 'none' : 'millisecond',
        attributes: { rating: metric.rating },
      });
    }
  }).catch(() => undefined);
};

export const captureFrontendException = (
  error: unknown,
  context?: {
    componentStack?: string | null;
    errorId?: string;
    extra?: Record<string, unknown>;
  },
): string | undefined => {
  try {
    if (typeof window === 'undefined') {
      return undefined;
    }

    loadSentry()
      .then((sentry) => {
        if (!sentry || !sentryInitialized) {
          return;
        }

        sentry.captureException(error, {
          extra: redactFrontendData({
            componentStack: context?.componentStack,
            errorId: context?.errorId,
            ...context?.extra,
          }),
          tags: {
            ...(context?.errorId ? { errorId: context.errorId } : {}),
          },
        });
      })
      .catch(() => undefined);

    return undefined;
  } catch {
    return undefined;
  }
};
